import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import {
  ListenTypInfo,
  SpsFortschritt,
  SpsImportListe,
  SpsListenUpdateErgebnis,
  leererSpsFortschritt,
  listenTypInfo
} from '../../../../../Models/Sps';
import { AuthService } from '../../../../../api-authorization/authorize.service';
import { ProjektDataService } from '../../../../../Services/offline/projekt-data.service';
import { SpsDataService } from '../../../../../Services/offline/sps-data.service';
import { SpsListenLoeschenService } from '../../../../../Services/sps-listen-loeschen.service';
import { SpsPruefungApiService } from '../../../../../Services/sps-pruefung-api.service';
import { Datenquelle } from '../../../../../Models/offline.model';
import { SpsListeComponent } from '../sps-liste/sps-liste.component';
import { SpsListenUpdateDialogComponent, SpsListenUpdateDialogData } from '../sps-listen-update-dialog/sps-listen-update-dialog.component';

/**
 * Übersicht aller importierten SPS-Listen eines Projekts (SPS-Zuordnungslisten
 * und PLC-Tag-Listen) mit Öffnen und Löschen – das Gegenstück zur Liste der
 * Messstellen- und Verbraucherlisten in der Projektübersicht.
 */
@Component({
  selector: 'app-sps-listen-uebersicht',
  templateUrl: './sps-listen-uebersicht.component.html',
  styleUrls: ['./sps-listen-uebersicht.component.css']
})
export class SpsListenUebersichtComponent implements OnInit, OnDestroy {

  projektId = 0;
  projektName = '';
  projektNummer = '';

  listen: SpsImportListe[] = [];
  fortschritt: SpsFortschritt = leererSpsFortschritt();

  laedt = false;
  fehler = '';

  /** Ids der Listen, deren Löschen gerade läuft (Ladezustand je Zeile). */
  private loeschend = new Set<number>();

  private subs = new Subscription();

  /** Woher die Listen stammen – offline aus dem heruntergeladenen Projekt. */
  quelle: Datenquelle = 'server';
  standUtc: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private daten: SpsDataService,
    private projektDaten: ProjektDataService,
    private loeschen: SpsListenLoeschenService,
    private authService: AuthService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.subs.add(
      this.route.paramMap.subscribe(params => {
        this.projektId = Number(params.get('projektId')) || 0;
        this.projektName = this.route.snapshot.queryParamMap.get('projektName') ?? '';
        this.laden();
        this.ladeProjekt();
      }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get canEdit(): boolean {
    return this.authService.canEdit();
  }

  get istOffline(): boolean {
    return this.quelle === 'offline';
  }

  /** Importieren, Aktualisieren und Löschen brauchen den Server. */
  get kannVerwalten(): boolean {
    return this.canEdit && !this.istOffline;
  }


  // =========================================================
  // LADEN
  // =========================================================

  laden(): void {
    if (!this.projektId) {
      this.fehler = 'Ungültige Projekt-Id.';
      return;
    }

    this.laedt = true;
    this.fehler = '';

    this.daten.getListen(this.projektId)
      .pipe(finalize(() => this.laedt = false))
      .subscribe({
        next: ergebnis => {
          this.listen = ergebnis.daten;
          this.quelle = ergebnis.quelle;
          this.standUtc = ergebnis.standUtc ?? null;
        },
        error: err => {
          this.listen = [];
          this.fehler = err?.status === 404
            ? 'Das Projekt existiert nicht oder ist nicht zugänglich.'
            : SpsPruefungApiService.fehlertext(err, err?.message ?? 'Die SPS-Listen konnten nicht geladen werden.');
        }
      });

    this.daten.getFortschritt(this.projektId).subscribe({
      next: f => this.fortschritt = f.daten,
      error: () => this.fortschritt = leererSpsFortschritt()
    });
  }

  private ladeProjekt(): void {
    if (!this.projektId) {
      return;
    }

    this.projektDaten.getProjekt(this.projektId).subscribe({
      next: p => {
        this.projektName = p.daten.name;
        this.projektNummer = p.daten.projektNummer;
      },
      error: () => { /* Kopfzeile bleibt ohne Projektname – die Listen laden unabhängig. */ }
    });
  }


  // =========================================================
  // ANZEIGE
  // =========================================================

  get zuordnungslisten(): SpsImportListe[] {
    return this.listen.filter(l => l.listenTyp === 'SpsZuordnungsliste');
  }

  get plcTagListen(): SpsImportListe[] {
    return this.listen.filter(l => l.listenTyp === 'PlcTagListe');
  }

  get eintraegeGesamt(): number {
    return this.listen.reduce((summe, l) => summe + l.eintraegeAnzahl, 0);
  }

  typInfo(liste: SpsImportListe): ListenTypInfo {
    return listenTypInfo(liste.listenTyp);
  }

  wirdGeloescht(liste: SpsImportListe): boolean {
    return this.loeschend.has(liste.id);
  }

  trackByListe(_index: number, l: SpsImportListe): number {
    return l.id;
  }


  // =========================================================
  // AKTIONEN
  // =========================================================

  oeffnen(liste: SpsImportListe): void {
    if (this.wirdGeloescht(liste)) {
      return;
    }

    void this.router.navigate(['/projekte', this.projektId, 'sps-listen', liste.id]);
  }

  /** Bestätigungsdialog mit Auswirkungen, dann Löschen; die Übersicht wird danach neu geladen. */
  loeschenStarten(liste: SpsImportListe, event?: Event): void {
    event?.stopPropagation();

    if (!this.kannVerwalten || this.wirdGeloescht(liste)) {
      return;
    }

    this.loeschend.add(liste.id);

    this.loeschen.loeschen(liste)
      .pipe(finalize(() => this.loeschend.delete(liste.id)))
      .subscribe(ergebnis => {
        if (ergebnis) {
          this.listen = this.listen.filter(l => l.id !== liste.id);
          this.fortschritt = ergebnis.fortschritt;
          this.laden();
        }
      });
  }

  /** „Liste aktualisieren“: neuere Excel-Version derselben Liste – Vorschau und Bestätigung im Dialog. */
  aktualisierenStarten(liste: SpsImportListe, event?: Event): void {
    event?.stopPropagation();

    if (!this.kannVerwalten || this.wirdGeloescht(liste)) {
      return;
    }

    const data: SpsListenUpdateDialogData = { liste };

    this.dialog.open<SpsListenUpdateDialogComponent, SpsListenUpdateDialogData, SpsListenUpdateErgebnis | null>(
      SpsListenUpdateDialogComponent, { data, width: '760px', maxWidth: '95vw', autoFocus: false, disableClose: true }
    ).afterClosed().subscribe(ergebnis => {
      if (!ergebnis) {
        return;
      }

      this.snackBar.open(SpsListeComponent.updateErgebnisText(ergebnis), 'OK', { duration: 7000 });
      this.fortschritt = ergebnis.fortschritt;
      this.laden();
    });
  }

  zumImport(): void {
    void this.router.navigate(['/projekte', this.projektId, 'import'], {
      queryParams: { projektName: this.projektName || undefined }
    });
  }

  zurSpsPruefung(): void {
    void this.router.navigate(['/projekte', this.projektId, 'sps-pruefung']);
  }

  zurueckZumProjekt(): void {
    void this.router.navigate(['/projekte', this.projektId]);
  }
}
